// The tracker charts' time axis and ranges: pure functions, kept apart from Chart.js so they can be tested.
import { format } from 'date-fns';

const HOUR = 3600 * 1000;
const DAY = 24 * HOUR;

export const RANGES = [
  { id: 'day', label: 'Last 24h', ms: DAY },
  { id: 'week', label: '7 days', ms: 7 * DAY },
  { id: 'all', label: 'Whole tracker', ms: null },
];

export const ts = (snapshot) => new Date(snapshot.snapshot_time).getTime();

/** The x range to show: the chosen span back from the latest point. A lone point gets an hour each side. */
export const rangeBounds = (seriesList, rangeId) => {
  const times = seriesList.filter((s) => s?.length).flatMap((s) => [ts(s[0]), ts(s[s.length - 1])]);
  if (!times.length) return null;
  const max = Math.max(...times);
  const earliest = Math.min(...times);
  const span = RANGES.find((r) => r.id === rangeId)?.ms;
  const min = span ? Math.max(earliest, max - span) : earliest;
  if (max - min < HOUR) return { min: max - HOUR, max: max + HOUR };
  return { min, max };
};

/**
 * Ticks that fit the range: hourly up to a day and a half, daily beyond; at most 10, never rotated.
 * In an hourly range that crosses midnight, the first tick and the first tick of each new day show
 * the date (ticks are stepped from the range's start, so midnight itself may not be a tick).
 */
export const timeScale = ({ min, max }) => {
  const span = Math.max(max - min, HOUR);
  const unit = span <= 36 * HOUR ? 'hour' : 'day';
  const hours = span / HOUR;
  const stepSize = unit === 'hour'
    ? (hours <= 8 ? 1 : hours <= 16 ? 2 : hours <= 24 ? 3 : 4)
    : Math.max(1, Math.ceil(span / DAY / 8));
  const crossesDays = new Date(min).toDateString() !== new Date(max).toDateString();
  return {
    type: 'time',
    min,
    max,
    time: { unit, tooltipFormat: 'd MMM yyyy, HH:mm' },
    grid: { color: '#eef0f3' },
    ticks: {
      stepSize,
      maxRotation: 0,
      autoSkip: true,
      maxTicksLimit: 10,
      callback: (value, index, ticks) => {
        const date = new Date(value);
        if (unit === 'day') return format(date, 'd MMM');
        const previous = index > 0 ? new Date(ticks[index - 1].value) : null;
        const newDay = previous ? previous.toDateString() !== date.toDateString() : crossesDays;
        return format(date, newDay ? 'd MMM HH:mm' : 'HH:mm');
      },
    },
  };
};
